import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../index.css';
import { useNavigate } from 'react-router-dom';

const GrapeContracts = () => {
  const [contracts, setContracts] = useState([]);
  const [weightTags, setWeightTags] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  console.log('Contracts:', contracts); // Debug log
  const [newContract, setNewContract] = useState({
    weightTagId: '',
    pricePerTon: 0,
    pickingCost: 0,
  });

  useEffect(() => {
    fetchContracts();
    fetchWeightTags();
  }, [selectedYear]);

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`/api/grape-contracts?year=${selectedYear}`);
      console.log('API Response:', response.data); // Debug log
      setContracts(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('Failed to load contracts');
      console.error('Error fetching contracts:', err);
      setContracts([]); // Ensure contracts is always an array
    } finally {
      setLoading(false);
    }
  };

  const fetchWeightTags = async () => {
    try {
      const response = await axios.get(`/api/weightTags?year=${selectedYear}`);
      console.log('Weight Tags Response:', response.data); // Debug log
      setWeightTags(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching weight tags:', err);
      setWeightTags([]); // Ensure weightTags is always an array
    }
  };

  const calculateTotal = (weightTagId, pricePerTon, pickingCost) => {
    const weightTag = weightTags.find(t => t.id === parseInt(weightTagId));
    if (!weightTag) return 0;
    
    const netWeight = weightTag.totalTons || 0;
    return (netWeight * pricePerTon) + (netWeight * pickingCost);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/grape-contracts', {
        ...newContract,
        year: selectedYear,
      });
      setShowAddModal(false);
      fetchContracts();
      setNewContract({
        weightTagId: '',
        pricePerTon: 0,
        pickingCost: 0,
      });
    } catch (err) {
      setError('Failed to create contract');
      console.error('Error creating contract:', err);
    }
  };

  if (loading) return (
    <div className="container mt-4">
      <div className="d-flex justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="container mt-4">
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    </div>
  );

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Grape Contracts</h2>
        <div className="d-flex gap-2">
          <select 
            className="form-select form-select-sm"
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(e.target.value);
              setError(null); // Clear any existing errors
              setLoading(true); // Show loading state while fetching new data
            }}
          >
            {Array.from({ length: 5 }, (_, i) => (
              <option key={i} value={new Date().getFullYear() - i}>
                {new Date().getFullYear() - i}
              </option>
            ))}
          </select>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={() => setShowAddModal(true)}
          >
            +Contract
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Date</th>
              <th>Grower</th>
              <th>Varietal</th>
              <th>Net Tons</th>
              <th>Price/Ton</th>
              <th>Picking</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((contract) => (
              <tr key={contract.id}>
                <td>{new Date(contract.date).toLocaleDateString()}</td>
                <td>{contract.growerName}</td>
                <td>{contract.varietal}</td>
                <td>{(Number(contract.netTons) || 0).toFixed(3)}</td>
                <td>${(Number(contract.pricePerTon) || 0).toFixed(2)}</td>
                <td>${(Number(contract.pickingCost) || 0).toFixed(2)}</td>
                <td>${(Number(contract.totalCost) || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="3"><strong>Totals:</strong></td>
              <td><strong>{(contracts.reduce((sum, c) => sum + (Number(c.netTons) || 0), 0)).toFixed(3)}</strong></td>
              <td></td>
              <td></td>
              <td><strong>${(contracts.reduce((sum, c) => sum + (Number(c.totalCost) || 0), 0)).toFixed(2)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Add Contract Modal */}
      {showAddModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show" style={{ display: 'block' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add New Contract</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setShowAddModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Weight Tag</label>
                      <select 
                        className="form-select"
                        value={newContract.weightTagId}
                        onChange={(e) => setNewContract({
                          ...newContract,
                          weightTagId: e.target.value
                        })}
                        required
                      >
                        <option value="">Select Weight Tag</option>
                        {weightTags.map((tag) => (
                          <option key={tag.id} value={tag.id}>
                            {tag.date} - {tag.growerName} - {tag.varietal} ({tag.totalTons || 0} tons)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Price per Ton</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={newContract.pricePerTon}
                        onChange={(e) => setNewContract({
                          ...newContract,
                          pricePerTon: parseFloat(e.target.value)
                        })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Picking Cost per Ton</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={newContract.pickingCost}
                        onChange={(e) => setNewContract({
                          ...newContract,
                          pickingCost: parseFloat(e.target.value)
                        })}
                        required
                      />
                    </div>

                    {newContract.weightTagId && (
                      <div className="alert alert-info">
                        <strong>Total Cost: </strong>
                        ${calculateTotal(
                          weightTags.find(t => t.id === newContract.weightTagId),
                          newContract.pricePerTon,
                          newContract.pickingCost
                        ).toFixed(2)}
                      </div>
                    )}

                    <div className="d-flex justify-content-end gap-2">
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => setShowAddModal(false)}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary">
                        Add Contract
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GrapeContracts;